import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

// TRIMY Shield Logo Component
const TrimyLogo = () => (
    <svg width="64" height="64" viewBox="0 0 48 48" fill="none">
        <defs>
            <linearGradient id="registerShieldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#00D4FF" />
                <stop offset="100%" stopColor="#12006d" />
            </linearGradient>
        </defs>
        <path 
            d="M24 4L6 10v12c0 11 8 18 18 22 10-4 18-11 18-22V10L24 4z" 
            fill="url(#registerShieldGrad)" 
        />
        <path 
            d="M24 12v6M24 22v2M18 16h12M16 22h4M28 22h4" 
            stroke="white" 
            strokeWidth="2" 
            strokeLinecap="round" 
        />
        <circle cx="24" cy="28" r="2" fill="white" />
        <path d="M20 32h8" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

export default function RegisterPage() {
    const [formData, setFormData] = useState({
        email: "",
        password: "",
        business_name: "",
        owner_name: "",
        phone: "",
    });
    const [loading, setLoading] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await register(formData);
            toast.success("Welcome to TRIMY! Your 7-day trial has started.");
            navigate("/dashboard");
        } catch (error) {
            toast.error(error.response?.data?.detail || "Registration failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="flex flex-col items-center mb-8">
                    <div className="mb-4">
                        <TrimyLogo />
                    </div>
                    <h1 className="font-heading text-3xl font-bold text-[#12006d] tracking-wide">TRIMY</h1>
                    <p className="text-gray-500 text-sm mt-1">Start your 7-day free trial</p>
                </div>

                <Card className="border-0 shadow-xl shadow-[#12006d]/5">
                    <CardHeader className="text-center pb-2">
                        <CardTitle className="font-heading text-xl text-[#12006d]">Create Account</CardTitle>
                        <CardDescription>Enter your business details to get started</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="business_name" className="text-gray-700">Business Name</Label>
                                <Input
                                    id="business_name"
                                    name="business_name"
                                    placeholder="Your Business Name"
                                    value={formData.business_name}
                                    onChange={handleChange}
                                    required
                                    data-testid="register-business-input"
                                    className="h-12 rounded-xl border-gray-200 focus:border-[#00D4FF] focus:ring-[#00D4FF]/20"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="owner_name" className="text-gray-700">Owner Name</Label>
                                <Input
                                    id="owner_name"
                                    name="owner_name"
                                    placeholder="Your Full Name"
                                    value={formData.owner_name}
                                    onChange={handleChange}
                                    required
                                    data-testid="register-owner-input"
                                    className="h-12 rounded-xl border-gray-200 focus:border-[#00D4FF] focus:ring-[#00D4FF]/20"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="phone" className="text-gray-700">Phone Number</Label>
                                <Input
                                    id="phone"
                                    name="phone"
                                    type="tel"
                                    placeholder="+233 20 000 0000"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    required
                                    data-testid="register-phone-input"
                                    className="h-12 rounded-xl border-gray-200 focus:border-[#00D4FF] focus:ring-[#00D4FF]/20"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-gray-700">Email</Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    placeholder="your@email.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    data-testid="register-email-input"
                                    className="h-12 rounded-xl border-gray-200 focus:border-[#00D4FF] focus:ring-[#00D4FF]/20"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-gray-700">Password</Label>
                                <Input
                                    id="password"
                                    name="password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    minLength={6}
                                    data-testid="register-password-input"
                                    className="h-12 rounded-xl border-gray-200 focus:border-[#00D4FF] focus:ring-[#00D4FF]/20"
                                />
                            </div>
                            <Button 
                                type="submit" 
                                className="w-full h-12 rounded-xl font-semibold text-white"
                                style={{ 
                                    background: 'linear-gradient(135deg, #00D4FF 0%, #12006d 100%)',
                                    boxShadow: '0 4px 12px rgba(0, 212, 255, 0.3)'
                                }}
                                disabled={loading}
                                data-testid="register-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Creating account...
                                    </>
                                ) : (
                                    "Start Free Trial"
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 text-center text-sm">
                            <span className="text-gray-500">Already have an account? </span>
                            <Link 
                                to="/login" 
                                className="text-[#00D4FF] font-medium hover:text-[#12006d] transition-colors"
                                data-testid="login-link"
                            >
                                Sign in
                            </Link>
                        </div>
                    </CardContent>
                </Card>
                
                {/* Footer */}
                <p className="text-center text-xs text-gray-400 mt-6">
                    © {new Date().getFullYear()} TRIMY. All rights reserved.
                </p>
            </div>
        </div>
    );
}
